// The LIB digital-resources console route subtree (console slice — ebooks /
// reading sessions / storage). Code-based routes per DEV-GUIDE-002 §4 — a
// package-composed subtree hand-writes `.lazy()` + `createLazyRoute`. This factory
// hangs the `/library/digital` tree off the portal's root route so router.tsx
// stays the single wiring point (composed ALONGSIDE the other lib subtrees — none
// touches another).
//
// GATING is per-screen (router.tsx's documented choice): the digital LAYOUT route
// re-checks lib:catalog.read via useSession/<Show> before it renders any child — a
// defence-in-depth mirror of the API's rbac('lib:catalog.read') guard on the ebook
// reads (writes ride lib:catalog; the SESSIONS + STORAGE analytics ride lib:reports
// at the API, so those two tabs additionally <Can>-gate lib:reports over their
// content — the layout gate only opens the console). The UI can only HIDE; the
// gateway is the authority (ADR-001 §14.2). Anonymous → /login;
// signed-in-without-the-grant → an explicit denied panel, never a blank Outlet.
//
// URL-as-state (DEV-GUIDE-002 §4.1): the two keyset lists (ebooks + sessions) own
// their filters in the URL through a self-healing validateSearch (a garbage
// `?status=`/`?active=` heals to undefined rather than throwing — zod is not a
// direct portal dep, so these are hand-written validators with the same contract
// as EbookListQuery/SessionListQuery). loaderDeps → `void prefetchInfiniteQuery`
// warms the SAME cache entry the screen's useInfiniteQuery re-keys (§4.3) WITHOUT
// holding the route (audit UX-01: an awaited ensureQueryData kept the outlet empty
// for the whole API wait); the screen paints its heading and its table bones from
// `queryState(...).pending()` while the page is on the wire. The ebook `search`
// LIKE-filter lives in the URL too (it is part of the single keyset list, NOT a
// separate FTS path — the catalogue omnibox precedent does not apply here).
//
// NO `cursor` IN EITHER URL (2026-09-22). Both lists are INFINITE STREAMS now:
// every facet and the ebook search still live in the URL — a shared link restores
// exactly the slice someone was reading — but the cursor does not, because it no
// longer names a page. It names a position inside one accumulating list, and a
// link to "everything up to cursor X" is a different amount of scrolling for
// every reader. A stale `?cursor=` on an old bookmark simply falls out, which is
// the self-healing contract these validators already had.
// `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is 1)
// into the same accumulating entry the screen reads, so the reader's scroll
// fetches page two, never the loader.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { libraryQueries } from "../../index.js";
import { panelSearch } from "@erp/ui/side-panel";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { HubGrid } from "@erp/ui/hub-grid";
import { PageHeading } from "@erp/ui/page-heading";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { Tablet } from "lucide-solid";
import type { AnyRoute } from "@tanstack/solid-router";

/* ─────────────────────────── ebook list URL-as-state ───────────────────────── */
export const EBOOKS_PAGE_SIZE = 20;

export interface EbooksSearch {
  search?: string;
  format?: string;
  license?: string;
  status?: string;
  library?: string;
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** Self-healing (§4.1): every facet is an OPEN string (the wire stores legacy text), so a bad value just drops to undefined. */
export function validateEbooksSearch(input: Record<string, unknown>): EbooksSearch {
  return {
    search: optionalString(input.search),
    format: optionalString(input.format),
    license: optionalString(input.license),
    status: optionalString(input.status),
    library: optionalString(input.library),
  };
}

/* ─────────────────────── reading-sessions list URL-as-state ────────────────── */
export const SESSIONS_PAGE_SIZE = 20;

export interface SessionsSearch {
  /** Tri-state: undefined = all, true = active only, false = ended only (SessionListQuery.active is a stringbool). */
  active?: boolean;
  device?: string;
}

/** Self-healing: `?active=` heals to undefined unless it is exactly a bool or the strings "true"/"false". */
export function validateSessionsSearch(input: Record<string, unknown>): SessionsSearch {
  const raw = input.active;
  const active =
    raw === true || raw === "true" ? true : raw === false || raw === "false" ? false : undefined;
  return {
    active,
    device: optionalString(input.device),
  };
}

/* ─────────────────────────── panel URL-state (the side-panel contract) ───────
 * `?panel=<name>&panelId=<id>&panelWide=1`, healed against a CLOSED set per
 * route (same discipline as the facet validators above): an unknown name — or a
 * stale id with no panel — drops to "no panel" rather than throwing, so a shared
 * link can never land on a half-open surface. */
export const EBOOK_DETAIL_PANELS = ["edit", "history"] as const;
export const SESSION_PANELS = ["start"] as const;

const DIGITAL_LINKS = [
  { to: "/library/digital/ebooks", label: "eBook catalogue", blurb: "Digital editions, licensing and seats", id: "digital-nav-ebooks" },
  { to: "/library/digital/sessions", label: "Reading sessions", blurb: "Who is reading what, and how it adds up", id: "digital-nav-sessions" },
  { to: "/library/digital/storage", label: "Storage", blurb: "File storage and bandwidth in use", id: "digital-nav-storage" },
] as const;

/**
 * The digital shell: gates on lib:catalog.read, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (module manifest) + header breadcrumbs.
 */
function DigitalLayout() {
  return (
    <ConsoleLayout
      perm="lib:catalog.read"
      deniedMessage="This account cannot open digital resources. Ask an administrator for catalogue access."
      skeletonId="digital-skeleton"
      deniedId="digital-denied"
      unreachableId="digital-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/digital` index — the landing with cards into each surface. */
function DigitalLanding() {
  return (
    <div>
      {/* The console's identity capsule (DEV-GUIDE-002 §12.5): every digital
          screen wears purple, so the landing they hang off wears it too. */}
      <PageHeading
        icon={Tablet}
        hue={MODULE_HUE.digital}
        title="Digital resources"
        subtitle="eBooks, who is reading them, and the storage they use"
        help={{
          title: "The library on screen",
          children: (
            <p>
              The eBooks the library licenses and lends without a shelf. The eBook catalogue is where an edition
              is added and its reading seats are set; Reading sessions shows who is reading what and for how
              long; Storage shows the files behind those editions and the bandwidth they use.
            </p>
          ),
        }}
      />
      {/* Was a hand-drawn grid of Link > Card > CardHeader with a green
          `StatusBadge label="Open"` for a button — see @erp/ui/hub-grid for
          why both were wrong, and for the one place that shape now lives. */}
      <HubGrid id="digital-landing" items={DIGITAL_LINKS} hue={MODULE_HUE.digital} columns={3} />
    </div>
  );
}

/**
 * Build the `/library/digital` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Static
 * `ebooks/new` is registered before the dynamic `ebooks/$ebookId` (path ranking
 * makes static win regardless; the order documents intent).
 */
export function makeLibDigitalRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const digitalRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/digital",
    component: DigitalLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "/",
    component: DigitalLanding,
  });

  const ebooksRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "ebooks",
    validateSearch: validateEbooksSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      search: search.search,
      format: search.format,
      license: search.license,
      status: search.status,
      library: search.library,
    }),
    loader: ({ context, deps }) => {
      // ONE definition serves loader + screen (§4.3). The filters object here MUST
      // equal the screen's so both hit the same cache entry.
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.ebooksInfinite({
          limit: EBOOKS_PAGE_SIZE,
          search: deps.search,
          format: deps.format,
          license: deps.license,
          status: deps.status,
          library: deps.library,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/digital/EbookCatalog.js")));

  const uploadEbookRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "ebooks/new",
  }).lazy(lazyRoute(() => import("../../screens/admin/digital/UploadEbook.js")));

  const ebookDetailRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "ebooks/$ebookId",
    // The record is a route PARAM; the URL's only search state is the edit panel.
    validateSearch: (input: Record<string, unknown>) => panelSearch(EBOOK_DETAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/digital/EbookDetail.js")));

  const sessionsRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "sessions",
    // facets (§4.1) alongside the start-session panel — one validator, both
    // halves self-healing. loaderDeps below reads ONLY the list facets, so
    // opening/closing the panel never re-runs the loader.
    validateSearch: (input: Record<string, unknown>) => ({
      ...validateSessionsSearch(input),
      ...panelSearch(SESSION_PANELS, input),
    }),
    loaderDeps: ({ search }) => ({ active: search.active, device: search.device }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        libraryQueries.readingSessionsInfinite({
          limit: SESSIONS_PAGE_SIZE,
          active: deps.active,
          device: deps.device,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/digital/ReadingSessions.js")));

  const storageRoute = createRoute({
    getParentRoute: () => digitalRoute,
    path: "storage",
  }).lazy(lazyRoute(() => import("../../screens/admin/digital/StorageMeters.js")));

  return [
    digitalRoute.addChildren([
      indexRoute,
      ebooksRoute,
      uploadEbookRoute,
      ebookDetailRoute,
      sessionsRoute,
      storageRoute,
    ]),
  ];
}
